import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Checkbox,
  IconButton,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  InputAdornment,
  LinearProgress,
  Button,
  Menu,
  ListItemIcon,
  ListItemText,
  Alert,
  Avatar,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SearchIcon from '@mui/icons-material/Search';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import Person from '@mui/icons-material/Person';

import { useSelector } from 'react-redux';
import { getOnboardingDashboard, updateEmployee } from '../../../../api/employeeApi';
import { getEntities } from '../../../../api/entityApi';
import { getLocations } from '../../../../api/locationApi';
import ClickableChip from '../../../../components/common/ClickableChip';
import OnboardingDetailsModal from './OnboardingDetailsModal';
import CustomNoResultsOverlay from '../employee-directory/components/CustomNoResultsOverlay';
import AlertDialog from '../../../../components/common/AlertDialog';
import useAlertDialog from '../../../../hooks/useAlertDialog';


const filterInputStyle = {
  backgroundColor: '#FFFFFF',
  borderRadius: '12px',
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    '& fieldset': {
      borderColor: 'rgba(117, 34, 209, 0.15)',
      transition: 'all 0.2s ease-in-out',
    },
    '&:hover fieldset': {
      borderColor: 'rgba(117, 34, 209, 0.3)',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#7522D1',
      borderWidth: '2px',
    },
  },
  '& .MuiInputLabel-root': {
    color: '#4F4937',
    '&.Mui-focused': {
      color: '#7522D1',
    },
  },
};

const stringToColor = (string) => {
  let hash = 0;
  for (let i = 0; i < string?.length || 0; i += 1) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }
  let color = "#";
  for (let i = 0; i < 3; i += 1) {
    const value = (hash >> (i * 8)) & 0xff;
    color += `00${value.toString(16)}`.slice(-2);
  }
  return color;
};

const JoiningForm = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useSelector((state) => state.auth);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [entityFilter, setEntityFilter] = useState("ALL");
  const [locationFilter, setLocationFilter] = useState("ALL");
  const [employeeTypeFilter, setEmployeeTypeFilter] = useState("ALL");
  
  const [entities, setEntities] = useState([]);
  const [locations, setLocations] = useState([]);
  
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [incompleteModal, setIncompleteModal] = useState(false);
  const { alertDialog, showAlert, closeAlert } = useAlertDialog();

  const openMenu = Boolean(anchorEl);

  // Define status options for ClickableChip
  const statusOptions = [
    { value: 'NOT_STARTED', label: 'Not Started' },
    { value: 'IN_PROGRESS', label: 'In Progress' },
    { value: 'COMPLETED', label: 'Completed' },
    { value: 'SUBMITTED', label: 'Submitted' },
    { value: 'UNDER_REVIEW', label: 'Under Review' },
    { value: 'REJECTED', label: 'Rejected' },
  ];

  // Define status options for HR Review (only PENDING and COMPLETED)
  const hrReviewStatusOptions = [
    { value: 'PENDING', label: 'Pending' },
    { value: 'COMPLETED', label: 'Completed' },
  ];

  // Fetch Entities (Backend filters by user scope)
  useEffect(() => {
    const fetchEntitiesList = async () => {
      const res = await getEntities();
      if (res.success) setEntities(res.data);
    };
    if (isAuthenticated) fetchEntitiesList();
  }, [user, isAuthenticated]);

  // Fetch Locations when entity is selected
  useEffect(() => {
    const fetchLocationsList = async () => {
      if (entityFilter !== 'ALL') {
        const res = await getLocations({ entityId: entityFilter });
        if (res.success) setLocations(res.data);
        setLocationFilter('ALL'); // Reset location when entity changes
      } else {
        setLocations([]);
        setLocationFilter('ALL');
      }
    };
    fetchLocationsList();
  }, [entityFilter]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // Pass current filters to the API
        const response = await getOnboardingDashboard({
          search: searchQuery,
          status: statusFilter,
          entityId: entityFilter,
          workLocation: locationFilter,
          employmentType: employeeTypeFilter,
          showInBgv: false,
          showInOfferStatus: false
        });

        if (response.success) {
          const formatted = response.data.map(emp => {
            const jf = emp.onboardingSummary?.joiningForm || {};
            const sections = jf.sections || {};
            return {
              id: emp.id,
              empNo: emp.employeeId,
              name: emp.name,
              email: emp.email,
              formStatus: (jf.status || "NOT_STARTED").toUpperCase().replace(" ", "_"),
              completionPercentage: jf.completionPercent || 0,
              startedDate: null, // Tracked differently or shown if percent > 0
              submittedDate: jf.status === "Submitted" ? new Date() : null,
              personalInfo: (sections.personalInfo || "NOT_STARTED").toUpperCase().replace(" ", "_"),
              bankDetails: (sections.bankDetails || "NOT_STARTED").toUpperCase().replace(" ", "_"),
              education: (sections.education || "NOT_STARTED").toUpperCase().replace(" ", "_"),
              employment: (sections.employment || "NOT_STARTED").toUpperCase().replace(" ", "_"),
              compensationBenefits: (emp.compensationStatus || "NOT_STARTED").toUpperCase().replace(" ", "_"),
              jobDetails: (emp.jobDetailsStatus || "NOT_STARTED").toUpperCase().replace(" ", "_"),
              taxDocuments: (sections.taxDocs || "NOT_STARTED").toUpperCase().replace(" ", "_"),
              emergencyContact: (sections.emergencyContact || "NOT_STARTED").toUpperCase().replace(" ", "_"),
              hrReview: (emp.hrReview || "PENDING").toUpperCase().replace(" ", "_"),
              documentsSubmitted: jf.documentsSubmitted || 0,
              documentsRequired: jf.documentsRequired || 0,
              expectedJoiningDate: emp.expectedJoiningDate,
            };
          });
          setEmployees(formatted);
        }
      } catch (error) {
        console.error("Error fetching joining form data:", error);
      } finally {
        setLoading(false);
      }
    };

    // Add a small delay for search query to avoid too many API calls
    const timeoutId = setTimeout(() => {
      fetchData();
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, statusFilter, entityFilter, locationFilter, employeeTypeFilter]);

  // Define columns for DataGrid
  const columns = [
    {
      field: "empNo",
      headerName: "EMPLOYEE NO.",
      width: 150,
      resizable: true,
      headerAlign: "left",
      align: "left",
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 500, color: "#7522D1" }}>
          {params.value || "-"}
        </Typography>
      ),
    },
    {
      field: "name",
      headerName: "NAME",
      width: 220,
      resizable: true,
      headerAlign: "left",
      align: "left",
      renderCell: (params) => {
        const name = params.value || "";
        const initials = name
          .split(" ")
          .map((n) => n[0])
          .join("")
          .substring(0, 2)
          .toUpperCase() || "EE";
        const avatarColor = stringToColor(name);
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Avatar
              sx={{
                width: 36,
                height: 36,
                fontSize: "0.85rem",
                fontWeight: 600,
                bgcolor: avatarColor,
                color: "#FFFFFF",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
              }}
            >
              {initials}
            </Avatar>
            <Typography variant="body2" sx={{ fontWeight: 600, color: "#030007" }}>
              {name}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: "email",
      headerName: "EMAIL",
      width: 180,
      resizable: true,
      headerAlign: "left",
      align: "left",
    },

    {
      field: "completion",
      headerName: "PROGRESS",
      width: 180,
      resizable: true,
      headerAlign: "left",
      align: "left",
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
          <LinearProgress
            variant="determinate"
            value={params.row.completionPercentage}
            sx={{
              width: 80,
              height: 6,
              borderRadius: 3,
              backgroundColor: '#F3EEFC',
              '& .MuiLinearProgress-bar': {
                backgroundColor: '#7522D1',
                borderRadius: 3,
              }
            }}
          />
          <Typography variant="body2" sx={{ fontWeight: 600, color: "#42374F", minWidth: 35 }}>
            {params.row.completionPercentage}%
          </Typography>
        </Box>
      ),
    },
    // {
    //   field: "documents",
    //   headerName: "DOCUMENTS",
    //   width: 180,
    //   resizable: true,
    //   headerAlign: "center",
    //   align: "left",
    //   renderCell: (params) => (
    //     <Typography variant="body2">
    //       {params.row.documentsSubmitted}/{params.row.documentsRequired}
    //     </Typography>
    //   ),
    // },
    {
      field: "personalInfo",
      headerName: "PERSONAL INFO",
      width: 180,
      resizable: true,
      headerAlign: "left",
      align: "left",
      renderCell: (params) => (
        <ClickableChip
          status={params.value}
          statusOptions={statusOptions}
          allowUpdate={false}  // Updates disabled for JoiningForm
          onStatusChange={(newStatus) => handleStatusChange(params.row.id, 'personalInfo', newStatus, params.value)}
        />
      ),
    },
    {
      field: "bankDetails",
      headerName: "BANK DETAILS",
      width: 180,
      resizable: true,
      headerAlign: "left",
      align: "left",
      renderCell: (params) => (
        <ClickableChip
          status={params.value}
          statusOptions={statusOptions}
          allowUpdate={false}
          onStatusChange={(newStatus) => handleStatusChange(params.row.id, 'bankDetails', newStatus, params.value)}
        />
      ),
    },
    {
      field: "education",
      headerName: "EDUCATION",
      width: 180,
      resizable: true,
      headerAlign: "left",
      align: "left",
      renderCell: (params) => (
        <ClickableChip
          status={params.value}
          statusOptions={statusOptions}
          allowUpdate={false}
          onStatusChange={(newStatus) => handleStatusChange(params.row.id, 'education', newStatus, params.value)}
        />
      ),
    },
    {
      field: "employment",
      headerName: "EMPLOYMENT",
      width: 180,
      resizable: true,
      headerAlign: "left",
      align: "left",
      renderCell: (params) => (
        <ClickableChip
          status={params.value}
          statusOptions={statusOptions}
          allowUpdate={false}
          onStatusChange={(newStatus) => handleStatusChange(params.row.id, 'employment', newStatus, params.value)}
        />
      ),
    },
    {
      field: "compensationBenefits",
      headerName: "CTC INFORMATION",
      width: 180,
      resizable: true,
      headerAlign: "left",
      align: "left",
      renderCell: (params) => (
        <ClickableChip
          status={params.value}
          statusOptions={statusOptions}
          allowUpdate={false}
          onStatusChange={(newStatus) => handleStatusChange(params.row.id, 'compensationBenefits', newStatus, params.value)}
        />
      ),
    },
    // {
    //   field: "jobDetails",
    //   headerName: "JOB DETAILS",
    //   width: 180,
    //   resizable: true,
    //   headerAlign: "center",
    //   align: "left",
    //   renderCell: (params) => (
    //     <ClickableChip
    //       status={params.value}
    //       statusOptions={statusOptions}
    //       allowUpdate={false}
    //       onStatusChange={(newStatus) => handleStatusChange(params.row.id, 'jobDetails', newStatus, params.value)}
    //     />
    //   ),
    // },
    // {
    //   field: "taxDocuments",
    //   headerName: "TAX DOCS",
    //   flex: 1,
    //   renderCell: (params) => (
    //     <ClickableChip 
    //       status={params.value} 
    //       statusOptions={statusOptions}
    //       allowUpdate={true}  // Updates enabled for JoiningForm
    //       onStatusChange={(newStatus) => handleStatusChange(params.row.id, 'taxDocuments', newStatus, params.value)}
    //     />
    //   ),
    // },
    // {
    //   field: "emergencyContact",
    //   headerName: "EMERGENCY CONTACT",
    //   flex: 1,
    //   renderCell: (params) => (
    //     <ClickableChip 
    //       status={params.value} 
    //       statusOptions={statusOptions}
    //       allowUpdate={true}  // Updates enabled for JoiningForm
    //       onStatusChange={(newStatus) => handleStatusChange(params.row.id, 'emergencyContact', newStatus, params.value)}
    //     />
    //   ),
    // },
    {
      field: "startedDate",
      headerName: "STARTED DATE",
      width: 180,
      resizable: true,
      headerAlign: "center",
      align: "left",
      valueGetter: (value) => value ? new Date(value).toLocaleDateString() : '-',
    },
    {
      field: "submittedDate",
      headerName: "SUBMITTED DATE",
      width: 180,
      resizable: true,
      headerAlign: "center",
      align: "left",
      valueGetter: (value) => value ? new Date(value).toLocaleDateString() : '-',
    },
    {
      field: "expectedJoiningDate",
      headerName: "EXPECTED JOINING DATE",
      width: 200,
      resizable: true,
      headerAlign: "center",
      align: "left",
      valueGetter: (value) => value ? new Date(value).toLocaleDateString() : '-',
    },
    {
      field: "hrReview",
      headerName: "HR REVIEW",
      width: 180,
      resizable: true,
      headerAlign: "center",
      align: "left",
      renderCell: (params) => (
        <ClickableChip
          status={params.value}
          statusOptions={hrReviewStatusOptions}
          allowUpdate={true}
          onStatusChange={(newStatus) => handleStatusChange(params.row.id, 'hrReview', newStatus, params.value)}
        />
      ),
    },
    {
      field: "actions",
      headerName: "ACTIONS",
      width: 100,
      resizable: false,
      sortable: false,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <IconButton size="small" onClick={(e) => handleMenuOpen(e, params.row)}>
          <MoreVertIcon />
        </IconButton>
      ),
    },
  ];

  // Data is now filtered in the backend
  const filteredData = employees;


  const handleStatusChange = async (rowId, field, newStatus, currentValue) => {
    try {
      // Check if trying to update HR Review to COMPLETED
      if (field === 'hrReview' && newStatus === 'COMPLETED') {
        // Find the employee data
        const employee = employees.find(emp => emp.id === rowId);
        if (employee && employee.compensationBenefits !== 'COMPLETED') {
          // Show incomplete fields modal instead of updating
          setSelectedRow(employee);
          setIncompleteModal(true);
          return;
        }
      }

      // Optimistic update
      setEmployees(prevEmployees =>
        prevEmployees.map(emp =>
          emp.id === rowId
            ? { ...emp, [field]: newStatus }
            : emp
        )
      );


      // Persist status changes
      let updateData = {};
      if (field === 'hrReview') updateData = { hrReview: newStatus };
      else if (field === 'compensationBenefits') updateData = { compensationStatus: newStatus };
      else if (field === 'jobDetails') updateData = { jobDetailsStatus: newStatus };

      if (Object.keys(updateData).length > 0) {
        const response = await updateEmployee(rowId, updateData);
        if (!response.success) {
          console.error(`Failed to update ${field} on backend`);
          // Revert on failure
          setEmployees(prevEmployees =>
            prevEmployees.map(emp =>
              emp.id === rowId
                ? { ...emp, [field]: currentValue }
                : emp
            )
          );
        }
      }
    } catch (error) {
      console.error("Error updating status:", error);
    }
  };

  const handleMenuOpen = (event, row) => {
    setAnchorEl(event.currentTarget);
    setSelectedRow(row);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const handleViewDetails = () => {
    if (selectedRow) {
      navigate(`/my-team/employee-directory/${selectedRow.id}`);
    }
    handleMenuClose();
  };

  const handleMoveToOnboarding = async () => {
    if (selectedRow.hrReview !== 'COMPLETED') {
      showAlert({
        title: 'Cannot Move to BGV',
        message: 'HR Review must be COMPLETED before moving to BGV',
        severity: 'warning'
      });
      return;
    }

    if (selectedRow.compensationBenefits === 'COMPLETED') {
      try {
        setLoading(true);
        // Move to BGV phase
        const response = await updateEmployee(selectedRow.id, { showInBgv: true });
        if (response.success) {
          // Refresh data with filters to ensure moved candidate disappears
          const updatedResponse = await getOnboardingDashboard({
            search: searchQuery,
            status: "ALL",
            showInBgv: false,
            showInOfferStatus: false
          });
          if (updatedResponse.success) {
            const formatted = updatedResponse.data.map(emp => {
              const jf = emp.onboardingSummary?.joiningForm || {};
              const sections = jf.sections || {};
              return {
                id: emp.id,
                empNo: emp.employeeId,
                name: emp.name,
                email: emp.email,
                formStatus: (jf.status || "NOT_STARTED").toUpperCase().replace(" ", "_"),
                completionPercentage: jf.completionPercent || 0,
                startedDate: null,
                submittedDate: jf.status === "Submitted" ? new Date() : null,
                personalInfo: (sections.personalInfo || "NOT_STARTED").toUpperCase().replace(" ", "_"),
                bankDetails: (sections.bankDetails || "NOT_STARTED").toUpperCase().replace(" ", "_"),
                education: (sections.education || "NOT_STARTED").toUpperCase().replace(" ", "_"),
                employment: (sections.employment || "NOT_STARTED").toUpperCase().replace(" ", "_"),
                compensationBenefits: (emp.compensationStatus || "NOT_STARTED").toUpperCase().replace(" ", "_"),
                jobDetails: (emp.jobDetailsStatus || "NOT_STARTED").toUpperCase().replace(" ", "_"),
                taxDocuments: (sections.taxDocs || "NOT_STARTED").toUpperCase().replace(" ", "_"),
                emergencyContact: (sections.emergencyContact || "NOT_STARTED").toUpperCase().replace(" ", "_"),
                hrReview: (emp.hrReview || "PENDING").toUpperCase().replace(" ", "_"),
                documentsSubmitted: jf.documentsSubmitted || 0,
                documentsRequired: jf.documentsRequired || 0,
                showInBgv: emp.showInBgv,
                showInOfferStatus: emp.showInOfferStatus,
              };
            });
            setEmployees(formatted);
          }
        }
      } catch (error) {
        console.error('Error moving to BGV status:', error);
      } finally {
        setLoading(false);
        handleMenuClose();
      }
    } else {
      setIncompleteModal(true);
    }
  };

  const navigateToDashboard = () => {
    if (selectedRow) {
      navigate(`/my-team/add-employee/${selectedRow.id}/compensation-benefits`);
    }
  }

  const handleCloseIncompleteModal = () => {
    setIncompleteModal(false);
    handleMenuClose();
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Grid container spacing={3}>
        {/* Filters Section */}
        <Grid item size={{ xs: 12 }}>
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: "16px",
              border: "1px solid rgba(117, 34, 209, 0.08)",
              backgroundColor: "#FFF",
              boxShadow: "0 4px 20px rgba(117, 34, 209, 0.02)",
            }}
          >
            <Grid container spacing={2}>
              <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                <TextField
                  placeholder="Search by Employee ID, Name, Email..."
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  sx={filterInputStyle}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: "rgba(117, 34, 209, 0.4)" }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {(user?.role?.hierarchyLevel === 'Level 1' || user?.role?.code === 'SUPER_ADMIN') && (
                <>
                  <Grid item size={{ xs: 12, sm: 6, md: 2.25 }}>
                    <FormControl size="small" fullWidth sx={filterInputStyle}>
                      <InputLabel>Entity</InputLabel>
                      <Select
                        value={entityFilter}
                        label="Entity"
                        onChange={(e) => setEntityFilter(e.target.value)}
                      >
                        <MenuItem value="ALL">All Entities</MenuItem>
                        {entities.map(ent => (
                          <MenuItem key={ent.id || ent._id} value={ent.id || ent._id}>{ent.name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item size={{ xs: 12, sm: 6, md: 2.25 }}>
                    <FormControl size="small" fullWidth disabled={entityFilter === 'ALL'} sx={filterInputStyle}>
                      <InputLabel>Work Location</InputLabel>
                      <Select
                        value={locationFilter}
                        label="Work Location"
                        onChange={(e) => setLocationFilter(e.target.value)}
                      >
                        <MenuItem value="ALL">All Locations</MenuItem>
                        {locations.map(loc => (
                          <MenuItem key={loc.id || loc._id} value={loc.id || loc._id}>{loc.name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </>
              )}

              <Grid item size={{ xs: 12, sm: 6, md: 2.25 }}>
                <FormControl size="small" fullWidth sx={filterInputStyle}>
                  <InputLabel>Employee Type</InputLabel>
                  <Select
                    value={employeeTypeFilter}
                    label="Employee Type"
                    onChange={(e) => setEmployeeTypeFilter(e.target.value)}
                  >
                    <MenuItem value="ALL">All Types</MenuItem>
                    <MenuItem value="FULL_TIME">Full Time</MenuItem>
                    <MenuItem value="PART_TIME">Part Time</MenuItem>
                    <MenuItem value="CONTRACT">Contract</MenuItem>
                    <MenuItem value="INTERN">Intern</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item size={{ xs: 12, sm: 6, md: 2.25 }}>
                <FormControl size="small" fullWidth sx={filterInputStyle}>
                  <InputLabel>Form Status</InputLabel>
                  <Select
                    value={statusFilter}
                    label="Form Status"
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <MenuItem value="ALL">All Status</MenuItem>
                    {statusOptions.map(opt => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* DataGrid Section */}
        <Grid item size={{ xs: 12 }}>
          <Box sx={{ width: "100%", overflow: "hidden" }}>
            <DataGrid
              rows={filteredData}
              columns={columns}
              loading={loading}
              checkboxSelection={false}
              disableRowSelectionOnClick
              rowHeight={64}
              slots={{
                noRowsOverlay: CustomNoResultsOverlay,
              }}
              sx={{
                height: 540,
                width: "100%",
                backgroundColor: "white",
                border: "none",
                borderRadius: "16px",
                boxShadow: "0 4px 20px rgba(117, 34, 209, 0.04)",
                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: "#F8F5FC",
                  borderBottom: "1px solid rgba(117, 34, 209, 0.08)",
                  "& .MuiDataGrid-columnHeaderTitle": {
                    fontWeight: 600,
                    color: "#42374F",
                    fontSize: "0.85rem",
                    letterSpacing: "0.5px",
                  },
                },
                "& .MuiDataGrid-row": {
                  alignItems: "center !important",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: "rgba(117, 34, 209, 0.02)",
                  },
                },
                "& .MuiDataGrid-cell": {
                  borderBottom: "1px solid rgba(117, 34, 209, 0.04)",
                  display: "flex",
                  alignItems: "center",
                },
                "& .MuiDataGrid-footerContainer": {
                  borderTop: "1px solid rgba(117, 34, 209, 0.08)",
                  backgroundColor: "#F8F5FC",
                },
              }}
              initialState={{
                pagination: { paginationModel: { pageSize: 10, page: 0 } },
              }}
              pageSizeOptions={[5, 10, 25, 50]}
            />
          </Box>
        </Grid>
      </Grid>


      {/* Dropdown Menu */}
      {/* <OnboardingDetailsModal
        open={detailsModalOpen}
        onClose={() => setDetailsModalOpen(false)}
        employeeId={selectedRow?.id}
      /> */}

      <Menu
        anchorEl={anchorEl}
        open={openMenu}
        onClose={handleMenuClose}
        PaperProps={{
          elevation: 2,
          sx: {
            borderRadius: 2,
            minWidth: 200,
            mt: 1,
            '& .MuiMenuItem-root': {
              py: 1.5,
              px: 2,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={handleViewDetails}
          sx={{
            '&:hover': {
              backgroundColor: 'rgba(117, 34, 209, 0.08)',
              color: '#7522D1',
              '& .MuiListItemIcon-root': {
                color: '#7522D1',
              },
            },
          }}
        >
          <ListItemIcon>
            <Person sx={{ fontSize: 20 }} />
          </ListItemIcon>
          <ListItemText primary="View Details" primaryTypographyProps={{ variant: 'body2' }} />
        </MenuItem>
        <MenuItem onClick={handleMoveToOnboarding}
          sx={{
            color: '#7522D1',
            '&:hover': {
              backgroundColor: 'rgba(117, 34, 209, 0.12)',
            },
          }}
        >
          <ListItemIcon>
            <PlayArrowIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Move to BGV" primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }} />
        </MenuItem>
      </Menu>

      {/* Incomplete Fields Modal */}
      <Dialog
        open={incompleteModal}
        onClose={handleCloseIncompleteModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            boxShadow: "0 10px 30px rgba(0, 0, 0, 0.1)",
          }
        }}
      >
        <DialogTitle sx={{ pb: 1, pt: 3, px: 3 }}>
          <Typography variant="h6" fontWeight={700} color="#030007">
            Incomplete Required Fields
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ px: 3, py: 2 }}>
          <Alert 
            severity="warning" 
            sx={{ 
              mb: 2.5, 
              borderRadius: "12px",
              backgroundColor: "rgba(245, 158, 11, 0.08)",
              color: "#B45309",
              border: "1px solid rgba(245, 158, 11, 0.15)",
              "& .MuiAlert-icon": { color: "#D97706" }
            }}
          >
            Please complete the following required fields before moving to onboarding:
          </Alert>
          <Box sx={{ mt: 1, p: 2, borderRadius: "12px", backgroundColor: "#FDFDFD", border: "1px dashed rgba(117, 34, 209, 0.15)" }}>
            {selectedRow && (
              <>
                {selectedRow.compensationBenefits !== 'COMPLETED' && (
                  <Typography variant="body2" sx={{ color: "#4F4937", fontWeight: 500 }}>
                    • <strong>CTC Information</strong> is currently: <em>{selectedRow.compensationBenefits?.replace("_", " ")}</em>
                  </Typography>
                )}
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 1.5 }}>
          <Button 
            onClick={navigateToDashboard} 
            variant="outlined"
            sx={{
              textTransform: "none",
              borderRadius: "10px",
              borderColor: "#7522D1",
              color: "#7522D1",
              fontWeight: 600,
              paddingX: 2.5,
              "&:hover": {
                borderColor: "#5C17A8",
                backgroundColor: "rgba(117, 34, 209, 0.04)",
              }
            }}
          >
            Update the Fields
          </Button>
          <Button 
            onClick={handleCloseIncompleteModal} 
            variant="contained"
            sx={{
              textTransform: "none",
              borderRadius: "10px",
              backgroundColor: "#7522D1",
              color: "#FFFFFF",
              fontWeight: 600,
              paddingX: 3,
              boxShadow: "0 4px 12px rgba(117, 34, 209, 0.2)",
              "&:hover": {
                backgroundColor: "#5C17A8",
                boxShadow: "0 6px 16px rgba(117, 34, 209, 0.3)",
              }
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* Alert Dialog */}
      <AlertDialog
        open={alertDialog.open}
        onClose={closeAlert}
        title={alertDialog.title}
        message={alertDialog.message}
        severity={alertDialog.severity}
        confirmText={alertDialog.confirmText}
        cancelText={alertDialog.cancelText}
        showCancel={alertDialog.showCancel}
        onConfirm={alertDialog.onConfirm}
      />
    </Box>
  );
};

export default JoiningForm;
